import { useCallback, useMemo, useState } from "react";
import type { CartState } from "../../hooks/useCart.js";
import { aggregateCartItems } from "../../lib/cart-aggregate.js";
import { messages } from "../../i18n/messages.js";
import { ErrorState } from "../ErrorState.js";
import { LoadingState } from "../LoadingState.js";
import { AppButton } from "../ui/AppButton.js";
import { DegradedCatalogBanner } from "../catalog/DegradedCatalogBanner.js";
import { CartEmptyState } from "./CartEmptyState.js";
import { CartLineItem } from "./CartLineItem.js";
import { CartTotals } from "./CartTotals.js";
import { PromoCodePanel, computeTg50Discount } from "./PromoCodePanel.js";

const PROMO_STORAGE_KEY = "asiastyle.miniapp.promo_code";

function readStoredPromo(): string | null {
  try {
    const raw = sessionStorage.getItem(PROMO_STORAGE_KEY);
    return raw?.trim() ? raw.trim().toUpperCase() : null;
  } catch {
    return null;
  }
}

function writeStoredPromo(code: string | null) {
  try {
    if (!code) sessionStorage.removeItem(PROMO_STORAGE_KEY);
    else sessionStorage.setItem(PROMO_STORAGE_KEY, code);
  } catch {
    /* ignore */
  }
}

interface CartViewProps {
  cartState: CartState;
  onBrowseCatalog: () => void;
  onCheckout: () => void;
  checkoutDisabled?: boolean;
  degradedMessage?: string | null;
}

export function CartView({
  cartState,
  onBrowseCatalog,
  onCheckout,
  checkoutDisabled = false,
  degradedMessage = null,
}: CartViewProps) {
  const { loading, mutating, error, cart, reload, setQty, removeItem, clearAll } = cartState;
  const [appliedCode, setAppliedCode] = useState<string | null>(() => readStoredPromo());

  const handleApplyPromo = useCallback(
    async (code: string) => {
      const normalized = code.trim().toUpperCase();
      if (normalized !== "TG50") {
        return {
          ok: false,
          message: messages.cart.promoUnknown,
          discount: 0,
        };
      }
      const subtotal = cart?.subtotal ?? 0;
      const discount = computeTg50Discount(subtotal, normalized);
      setAppliedCode(normalized);
      writeStoredPromo(normalized);
      if (discount <= 0) {
        return {
          ok: true,
          message: messages.cart.promoTg50UnderMin(500),
          discount: 0,
        };
      }
      return {
        ok: true,
        message: messages.cart.promoTg50Applied(discount),
        discount,
      };
    },
    [cart?.subtotal],
  );

  const handleRemovePromo = useCallback(() => {
    setAppliedCode(null);
    writeStoredPromo(null);
  }, []);

  const displayTotals = useMemo(() => {
    const subtotal = cart?.subtotal ?? 0;
    const baseDiscount = cart?.discount_total ?? 0;
    const promo = computeTg50Discount(subtotal, appliedCode);
    const discountTotal = appliedCode ? Math.max(baseDiscount, promo) : baseDiscount;
    const total = Math.max(0, subtotal - discountTotal);
    return { subtotal, discountTotal, total, currency: cart?.currency ?? "UAH" };
  }, [cart?.subtotal, cart?.discount_total, cart?.currency, appliedCode]);

  if (loading && !cart) {
    return <LoadingState label={messages.cart.loading} />;
  }

  if (error && !cart) {
    return <ErrorState message={error} onRetry={reload} />;
  }

  const rawItems = cart?.items ?? [];
  const items = aggregateCartItems(rawItems);
  const isEmpty = items.length === 0;

  return (
    <section className="flex flex-1 flex-col pb-64">
      <div className="flex items-center justify-between px-4 pt-4">
        <h2 className="text-lg font-semibold">{messages.cart.title}</h2>
        {!isEmpty ? (
          <span className="text-sm opacity-60">{messages.cart.itemsCount(cart?.item_count ?? 0)}</span>
        ) : null}
      </div>

      {degradedMessage ? (
        <div className="px-4 pt-3">
          <DegradedCatalogBanner message={degradedMessage} />
        </div>
      ) : null}

      {error ? <p className="px-4 text-sm text-red-600">{error}</p> : null}

      {isEmpty ? (
        <CartEmptyState onBrowseCatalog={onBrowseCatalog} />
      ) : (
        <>
          <ul className="flex flex-col gap-3 px-4 py-3" role="list">
            {items.map((item) => (
              <li key={item.product_id}>
                <CartLineItem
                  item={item}
                  disabled={mutating}
                  onIncrease={() => void setQty(item.product_id, item.qty + 1)}
                  onDecrease={() => {
                    if (item.qty <= 1) {
                      void removeItem(item.product_id);
                      return;
                    }
                    void setQty(item.product_id, item.qty - 1);
                  }}
                  onRemove={() => void removeItem(item.product_id)}
                />
              </li>
            ))}
          </ul>

          {/* FUNCTIONALITY-095: TG50 panel inline in cart scroll (before checkout bar) */}
          <div className="space-y-2 px-4 pb-4">
            {import.meta.env.VITE_BUILD_ID ? (
              <p className="text-[10px] opacity-40">promo · {import.meta.env.VITE_BUILD_ID}</p>
            ) : null}
            <PromoCodePanel
              subtotal={displayTotals.subtotal}
              disabled={mutating}
              appliedCode={appliedCode}
              onApply={handleApplyPromo}
              onRemove={handleRemovePromo}
            />
            <CartTotals
              subtotal={displayTotals.subtotal}
              discountTotal={displayTotals.discountTotal}
              total={displayTotals.total}
              currency={displayTotals.currency}
            />
          </div>

          <div className="fixed bottom-[calc(4.75rem+env(safe-area-inset-bottom))] left-1/2 z-20 w-full max-w-[520px] -translate-x-1/2 space-y-2 px-4 pb-1">
            <div className="grid grid-cols-2 gap-2">
              <AppButton variant="secondary" disabled={mutating} onClick={() => void clearAll()}>
                {messages.cart.clear}
              </AppButton>
              <AppButton
                disabled={mutating || checkoutDisabled}
                onClick={onCheckout}
              >
                {checkoutDisabled ? messages.cart.checkoutUnavailable : messages.cart.checkout}
              </AppButton>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
